// Minimal web i18n for the share SPA. English is the default/source language;
// RU and UK parity lives in the `ru`/`uk` dicts below for users whose browser
// reports those locales (navigator.language). Locale can also be set explicitly
// via setLocale() and is persisted to localStorage (key 'bw-web-locale').
// Mirrors the desktop convention (app/lib/i18n): dotted keys, {var}
// interpolation, EN fallback on miss.

type Dict = Record<string, string>;

const en: Dict = {
  'loginGate.title': 'Private recording',
  'loginGate.subWithTeam': '«{teamName}» shared this with you',
  'loginGate.subNoTeam': 'Sign in to listen',
  'loginGate.emailPlaceholder': 'your@email.com',
  'loginGate.requestBtn': 'Get code',
  'loginGate.requestError': "Couldn't send the code. Try again.",
  'loginGate.rateLimitRetry': 'Too many requests — try again in {wait}.',
  'common.minutesShort': '{n} min',
  'common.secondsShort': '{n} sec',
  'loginGate.codeSent': 'Code sent to {email}',
  'loginGate.codePlaceholder': '6-digit code',
  'loginGate.verifyBtn': 'Sign in',
  'loginGate.invalidCode': 'Invalid code',
  'loginGate.codeExpired': 'Code expired — request a new one',
  'loginGate.tooManyAttempts': 'Too many attempts — request a new code',
  // library
  'library.title': 'Team library',
  // Подпись шапки — ОБОЛОЧКА: счётчики приезжают в неё уже словами, потому
  // что английскому нужна форма единственного числа («1 song»), а ru/uk — нет.
  // Порядок и разделители остаются у каждого языка своими.
  'library.headSummary': 'Team library · {songs} · {setlists}',
  'library.headSongs': '{n} songs',
  'library.headSongsOne': '1 song',
  'library.headSetlists': '{n} setlists',
  'library.headSetlistsOne': '1 setlist',
  'library.myTeam': 'My team',
  'library.empty': "You're not on any team yet",
  'library.nothingPublished': 'Nothing published yet',
  'library.untitled': 'Untitled',
  'library.loadError': "Couldn't load library",
  'library.viewersBtn': 'Viewers',
  'library.noViewers': 'No one has opened this yet',
  'library.viewersError': "Couldn't load viewers",
  'library.revokeBtn': 'Revoke',
  'library.revoked': 'Revoked',
  'library.viewerRow': '{email} · {lastSeen} · {source}',
  'library.sourceTeam': 'team',
  'library.sourceGrant': 'direct',
  'library.visPublic': 'Public link',
  'library.visTeam': 'Team only',
  'library.visHint': 'Who can open this version',
  'library.visOwnerNote': 'Only the version owner sees this',
  'library.lastSeenNever': 'never',
  'library.visSaved': 'Saved',
  'library.visError': "Couldn't save",
  // W6 library keys
  'library.tabSetlists': 'Setlists',
  'library.tabSongs': 'Songs',
  // Волна E, задача 4: две подписанные группы сет-листов вместо одной
  // «Past». `library.pastHeading` уехал вместе с ней — data-role строки
  // остался `past-heading`, сменилась только подпись.
  'library.nextHeading': 'Next',
  'library.earlierHeading': 'Earlier',
  'library.setlistSongs': '{n} songs',
  'library.setlistSongsOne': '1 song',
  'library.teamSwitcherLabel': 'Team',
  'library.setlistsEmpty': 'No setlists yet',
  'library.relToday': 'today',
  'library.relTomorrow': 'tomorrow',
  'library.relYesterday': 'yesterday',
  'library.relInDays': 'in {n} days',
  'library.relDaysAgo': '{n} days ago',
  // Волна G: офлайн-кэш — строка свежести в шапке и стирание данных
  'library.freshSyncing': 'Updating…',
  'library.freshJustNow': 'Updated just now',
  'library.freshAt': 'Updated {when}',
  'library.freshOfflineAt': 'Offline · updated {when}',
  'library.eraseOffline': 'Erase offline data',
  'library.eraseOfflineConfirm': 'Erase?',
  'library.eraseOfflineCancel': 'Keep',
  'library.eraseOfflineDone': 'Offline data erased',
  // W12 visual-redesign keys
  'library.songVersions': '{n} versions',
  'library.songVersionsOne': '1 version',
  'library.songSubLatest': 'Latest {date}',
  'library.songCount': '{n} songs',
  'library.searchPlaceholder': 'Search songs',
  'library.songsNoMatch': 'No songs match your search',
  'library.nothingPublishedBody': 'Songs your team publishes will show up here.',
  // Wave C — songs-panel sort switcher (Recent | A–Z). English in all three
  // dicts (invariant 8): the caption explains WHY a song can move without the
  // viewer's own action, so it must stay legible to every locale reader.
  'library.sortRecent': 'Recent',
  'library.sortAlpha': 'A–Z',
  'library.sortCaption': 'Last opened by anyone in the team',
  // Wave C — song row: words body (default entry) + separate audio-entry keys
  'library.noWords': 'no words',
  'library.wordsNotPublished': "Words haven't been published from the desktop app yet",
  'library.wordsOpened': 'Opened {when}',
  // Волна E — пара органов строки (song-row-controls.ts). Две подписи имён
  // (их несёт aria-label: на экране орган — одна иконка) и две причины, по
  // которым слот пуст: пунктирный слот ОБЯЗАН называть отсутствие, иначе это
  // немой погашенный контрол. `library.audioEntryListen` уехал вместе с
  // текстовой кнопкой «Listen», которую волна E убрала из строки.
  'library.wordsEntry': 'Words',
  'library.audioEntry': 'Listen',
  // Раскрывашка: имя обязано отличаться от «играть» — слот с несколькими
  // записями не ведёт в плеер, а раскрывает список прямо в строке.
  'library.audioVersions': 'Recordings',
  'library.noWordsSlot': 'No words published for this song yet',
  'library.noAudioSlot': 'No recordings published for this song yet',
  // Волна F, задача 6 — лист управления песней и вход в него. Имя кнопки «⋯»
  // без имени песни, как у соседних слотов (`wordsEntry`/`audioEntry`): на
  // экране это один из трёх органов строки, а не самостоятельная поверхность.
  // Доступное имя САМОГО листа имя песни несёт — лист накрывает строку, из
  // которой пришёл, и без него остался бы «настройками неизвестно чего».
  'library.songSheetBtn': 'Song settings',
  'library.songSheetTitle': 'Song settings — {song}',
  'library.songSheetOwnerNote': 'You published this song',
  'library.songSheetClose': 'Close',
  // ── Волна F, задача 7: раздел «Слова» в листе управления песней ─────────
  'library.wordsVisHeading': 'Who can see the words',
  'library.wordsVisTeam': 'Team only',
  'library.wordsVisPublic': 'Anyone with the link',
  'library.wordsVisExplainTeam': 'Only people on this team can open these words. Anyone else is asked to sign in.',
  'library.wordsVisExplainPublic': 'Anyone who has the link can open these words, including people outside the team. Switch back to Team only and the link you already sent stops opening.',
  'library.wordsLinkLabel': 'Link to these words',
  'library.wordsLinkCopy': 'Copy',
  'library.wordsLinkCopied': 'Copied',
  'library.wordsLinkCopyFailed': "Couldn't copy — select the address above and copy it yourself.",
  'library.wordsNeverOpened': 'No one has opened this yet',
  'library.wordsRemoveBtn': 'Remove words',
  'library.wordsRemoveWarning': 'Remove these words from the library? The link you already sent stops opening, for everyone.',
  'library.wordsRemoveConfirm': 'Remove',
  'library.wordsRemoveCancel': 'Keep them',
  'library.wordsErrAuth': 'Your session expired — sign in again.',
  'library.wordsErrForbidden': "These words aren't yours to change.",
  'library.wordsErrGone': 'These words are no longer published.',
  'library.wordsErrNetwork': "Couldn't reach the server — try again.",
  // ── Волна F, задача 8: раздел «Записи» в листе управления песней ────────
  // Надзаголовок называет ПРЕДМЕТ («Recordings»), а имя выбора первой записи
  // стоит на самой группе радио: раздел делает больше, чем выбор главной —
  // он же про видимость и снятие, — и заявлять одно вместо трёх было бы
  // неправдой.
  'library.recordingsHeading': 'Recordings',
  'library.recordingsDefaultLabel': 'Which recording opens first',
  'library.recordingsDefaultBadge': 'Opens first',
  'library.recordingsAdded': 'added {when}',
  'library.recordingsVisLabel': 'Who can open this recording',
  'library.recordingsVisTeam': 'Team only',
  'library.recordingsVisPublic': 'Anyone with the link',
  'library.recordingsVisError': "Couldn't save — try again.",
  'library.recordingsVisReading': 'Reading who can open it…',
  'library.recordingsVisUnknown': "Couldn't read who can open it.",
  'library.recordingsRemoveBtn': 'Remove recording',
  'library.recordingsRemoveWarning': 'Remove this recording from the library? The link you already sent stops playing, for everyone.',
  'library.recordingsRemoveConfirm': 'Remove',
  'library.recordingsRemoveCancel': 'Keep it',
  'library.recordingsErrAuth': 'Your session expired — sign in again.',
  'library.recordingsErrForbidden': "This recording isn't yours to change.",
  'library.recordingsErrGone': 'This recording is no longer in the library.',
  'library.recordingsErrRevoked': 'This recording has been taken down, so it cannot open first.',
  'library.recordingsErrNoSong': 'This recording was published on its own, without a song, so it cannot open first.',
  'library.recordingsErrNetwork': "Couldn't reach the server — try again.",
  // ── Волна F, задача 8: раздел «Кто открывал» ────────────────────────────
  // Раздел живёт только у ЗАПИСЕЙ: у слов имён нет и не будет (решение схемы
  // 0018 — «для команды из четырёх человек это слежка»). «Team» вместо кнопки
  // отзыва — не заглушка, а ответ: доступ участнику даёт команда, и отнять его
  // одной песней нечем.
  'library.sheetViewersHeading': 'Who has opened it',
  'library.sheetViewersEmpty': 'No one has opened this yet.',
  'library.sheetViewersError': "Couldn't load who has opened this.",
  'library.sheetViewersRevoke': 'Remove',
  'library.sheetViewersTeamNote': 'Team',
  'library.sheetViewersRevokeError': "Couldn't remove access — try again.",
  'library.sheetViewersNever': 'Never opened',
  'library.sheetViewersTimes': '{n} times',
  'library.sheetViewersTimesOne': 'once',
  'library.sheetViewersByLink': 'by link',
  'library.setlistsEmptyBody': 'Setlists shared with your team will appear here.',
  // Wave C completeness pass — loading skeleton, offline/connection-lost
  // states, and the "no words anywhere" explanation.
  'library.loading': 'Loading your library…',
  'library.offlineTitle': 'No connection',
  'library.offlineBody': 'Check your internet connection and try again.',
  'library.retryBtn': 'Try again',
  'library.connectionLostBanner': 'Connection lost — this list may be out of date.',
  'library.noWordsYet': 'No words published yet',
  'library.noWordsYetBody': 'Words are published from the desktop app, not the web — publish a chart there and it will show up here.',
  // Wave D task 6 — owner-only "Import words from my setlist links" action,
  // hung off the "no words anywhere" notice above.
  'library.importWordsBtn': 'Import words from my setlist links',
  'library.importWordsRunning': 'Importing…',
  'library.importWordsSuccess': 'Imported words for {n} songs.',
  'library.importWordsError': "Couldn't import your words — try again.",
  // Distinct from importWordsError on purpose (sweeping cross-review, wave D):
  // a 401 means the session expired, and "try again" would just hit the same
  // 401 forever — the action here leads to sign-in instead of retrying.
  'library.importWordsAuthError': 'Your session expired — sign in to import your words.',
  'library.importZeroNoLinks': "You don't have any live setlist links to import words from.",
  'library.importZeroUpToDate': 'Your words are already up to date — nothing new to import.',
  'library.importZeroOldLinks': 'Your setlist links are too old to read words from — republish them from the desktop app.',
  'library.importZeroDataGone': "Couldn't read the words from your links — try republishing from the desktop app.",
  'library.importZeroGeneric': "Couldn't find new words to import this time.",
  'common.backToLibrary': 'Back to library',
  'setlist.emptyBody': 'No songs have been added to this setlist yet.',
  'setlist.notFoundBody': 'The link may have been revoked or the setlist deleted.',
  'setlist.loadErrorBody': 'Please check your connection and try again.',
  // setlist
  'setlist.empty': 'This setlist is empty',
  'setlist.railHeading': 'Setlist',
  'setlist.untitled': 'Untitled',
  'setlist.trackRow': '{pos}. {name}',
  'setlist.loadError': "Couldn't load setlist",
  'setlist.notFound': 'This setlist is no longer available',
  // Единый сетлист, фаза 1 (setlist-screen/view.ts) — экран, которым теперь
  // открывается ЛЮБОЙ сетлист из библиотеки: шапка, лайв-пилюля, пустое
  // состояние. Строки песен переиспользуют library.wordsEntry/audioEntry/
  // audioVersions/noWordsSlot/noAudioSlot — своего словаря для них нет.
  'setlistScreen.back': 'Library',
  'setlistScreen.live': 'Live · song {n}',
  'setlistScreen.now': 'Now',
  'setlistScreen.empty': 'No songs in this setlist yet',
  // ── Единый сетлист, фаза 3 (design gate G2) — гостевой сетлист по ключу
  // (`/s/<id>?g=<key>`, без входа), закрытый доступ, выбор версии в плеере и
  // рельса/страница «только слова». Английские строки во всех трёх словарях —
  // инвариант 8.
  'guestSetlist.captionAudioTeam': 'Words are open to anyone with the link. Audio is for the team after sign-in.',
  'guestSetlist.captionWordsTeam': 'Audio is open to anyone with the link. Words are for the team after sign-in.',
  'guestSetlist.audioLocked': 'Audio is for the team — sign in',
  'guestSetlist.closedTitle': 'This link is closed',
  'guestSetlist.closedBodyNamed': 'The leader closed guest access to «{name}». If you’re on the team, sign in — the setlist is in your library.',
  'guestSetlist.closedBodyUnnamed': 'The leader closed guest access. If you’re on the team, sign in — the setlist is in your library.',
  'versionPicker.trigger': 'Version · {label}',
  'versionPicker.ariaLabel': 'Versions',
  'versionPicker.newest': 'newest',
  'versionPicker.unlabeled': 'Recording',
  'versionPicker.singleMeta': '{label} · {date}',
  'versionPicker.footnote': 'Your choice is saved on this device.',
  'versionPicker.sheetTitle': 'Versions',
  'wordsOnly.chip': 'No recording — words only',
  // library.wordsTag — задача 11 (строка библиотеки со звуком и словами одним
  // списком), заведён здесь заранее по брифу задачи 9.
  'library.wordsTag': 'Words',
  // dashboard
  'dashboard.back': '← Back to library',
  'dashboard.quotaLabel': '{used} MB of {limit} MB used',
  'dashboard.joinCode': 'Join code',
  'dashboard.copy': 'Copy',
  'dashboard.copied': 'Copied',
  'dashboard.copyFailed': "Couldn't copy",
  'dashboard.members': 'Members · {n}',
  'dashboard.roleOwner': 'Owner',
  'dashboard.roleMember': 'Member',
  'dashboard.loadError': "Couldn't load your team.",
  'dashboard.unknownEmail': '(unknown)',
  'dashboard.joinTitle': 'Join a team',
  'dashboard.joinHint': 'Enter the 8-character join code from your team owner',
  'dashboard.joinPlaceholder': 'Join code',
  'dashboard.joinBtn': 'Join team',
  'dashboard.leaveBtn': 'Leave team',
  'dashboard.youSuffix': '· you',
  'dashboard.joinErrorInvalid': 'Invalid or expired join code',
  'dashboard.joinErrorGeneric': 'Could not join — try again',
  'dashboard.rotate': 'Rotate',
  'dashboard.inviteTitle': 'Invite by email',
  'dashboard.invitePlaceholder': 'bandmate@example.com',
  'dashboard.inviteBtn': 'Send invite',
  'dashboard.inviteSent': 'Invite sent',
  'dashboard.inviteError': "Couldn't send — try again",
  'dashboard.inviteLimit': 'Too many invites today — you can send more tomorrow.',
  'dashboard.removeMember': 'Remove member',
  // admin
  'admin.title': 'Admin',
  'admin.nav.overview': 'Overview',
  'admin.nav.users': 'Users',
  'admin.nav.teams': 'Teams',
  'admin.usersSoon': 'User management — coming soon',
  'admin.titleTeams': 'Admin — teams',
  'admin.notAuthorized': 'Not authorized. Sign in as an admin.',
  'admin.signIn': 'Sign in',
  'admin.loadError': "Couldn't load — try again.",
  'admin.retry': 'Retry',
  'admin.actionFailed': 'Action failed — try again',
  'admin.storageQuota': 'Storage quota',
  'admin.setQuota': 'Set quota',
  'admin.membersHeading': 'Members',
  'admin.noMembers': 'No members',
  'admin.sharesHeading': 'Shares',
  'admin.noShares': 'No active shares',
  'admin.back': '← Teams',
  'admin.deactivate': 'Deactivate',
  'admin.activate': 'Activate',
  'admin.remove': 'Remove',
  'admin.revoke': 'Revoke',
  'admin.membersCount': '{n} members',
  'admin.teamRow': '{name} · {owner} · {used} / {limit} · {members}',
  'admin.memberRow': '{email} · {role}',
  'admin.memberRowDeactivated': '{email} · {role} · DEACTIVATED',
  'admin.shareRow': '{name} · {size}',
  'common.unknown': 'unknown',
  'common.mb': 'MB',
  // admin — allowlist (Users/Access section)
  'admin.allowlistHeading': 'Users / Access',
  'admin.allowlistEmailPlaceholder': 'email@example.com',
  'admin.allowlistAdd': 'Add',
  'admin.allowlistRemove': 'Remove',
  'admin.allowlistActivated': 'activated',
  'admin.allowlistEmpty': 'Allowlist is empty',
  'admin.allowlistAddedAt': 'Added {date}',
  // admin — access-requests card (Telegram gate — cold signups awaiting a decision)
  'admin.pending.title': 'Access requests',
  'admin.pending.approve': 'Approve',
  'admin.pending.reject': 'Reject',
  'admin.pending.empty': 'No access requests',
  // admin — users page (Phase 2)
  'admin.users.col.email': 'Email',
  'admin.users.col.name': 'Name',
  'admin.users.col.status': 'Status',
  'admin.users.col.lang': 'Lang',
  'admin.users.col.signup': 'Signed up',
  'admin.users.col.activated': 'Activated',
  'admin.users.col.devices': 'Devices',
  'admin.users.col.teams': 'Teams',
  'admin.users.statusActive': 'active',
  'admin.users.statusWaitlist': 'allowed',
  'admin.users.statusPending': 'requested',
  'admin.users.empty': 'No users yet',
  'admin.users.searchPlaceholder': 'Search by email…',
  'admin.users.loadError': "Couldn't load users — try again.",
  'admin.users.notAuthorized': 'Not authorized. Sign in as an admin.',
  'admin.users.statusRevoked': 'revoked',
  'admin.users.grant': 'Grant access',
  'admin.users.revoke': 'Revoke access',
  'admin.users.invite': 'Invite',
  'admin.users.inviteSent': 'Invite sent',
  'admin.users.inviteFailed': "Couldn't send invite — try again",
  'admin.users.actionError': 'Action failed — try again',
  // admin — delete user (Phase 5)
  'admin.users.delete': 'Delete user',
  'admin.users.confirmTitle': 'Delete user "{email}"?',
  'admin.users.confirmInstruction': 'Type the exact email address to confirm:',
  'admin.users.confirmPlaceholder': 'Email address',
  'admin.users.confirmBtn': 'Delete permanently',
  'admin.users.confirmCancel': 'Cancel',
  'admin.users.deleteError': 'Delete failed — try again',
  'admin.users.ownsTeamsError': 'This user owns one or more teams. Delete or transfer their teams first.',
  // admin — teams page (Phase 4)
  'admin.teams.col.name': 'Team',
  'admin.teams.col.owner': 'Owner',
  'admin.teams.col.storage': 'Storage',
  'admin.teams.col.members': 'Members',
  'admin.teams.empty': 'No teams yet',
  'admin.teams.searchPlaceholder': 'Search by name…',
  'admin.teams.loadError': "Couldn't load teams — try again.",
  'admin.teams.notAuthorized': 'Not authorized.',
  'admin.teams.delete': 'Delete team',
  'admin.teams.confirmTitle': 'Delete team "{name}"?',
  'admin.teams.confirmInstruction': 'Type the team name to confirm:',
  'admin.teams.confirmPlaceholder': 'Team name',
  'admin.teams.confirmBtn': 'Delete permanently',
  'admin.teams.confirmCancel': 'Cancel',
  'admin.teams.deleteError': 'Delete failed — try again',
  // admin — nav (Phase 6)
  'admin.nav.observability': 'Observability',
  // admin — overview page (Phase 6)
  'admin.overview.heading': 'Overview',
  'admin.overview.audience': 'Audience',
  'admin.overview.storage': 'Storage · Limits',
  'admin.overview.integrations': 'Integrations',
  'admin.overview.integ.sentry': 'Sentry',
  'admin.overview.integ.posthog': 'PostHog',
  'admin.overview.integ.d1': 'D1',
  'admin.overview.integ.live': 'live',
  'admin.overview.integ.notConfigured': 'not configured — check secret',
  // admin — plural helpers (EN: no inflection needed; RU below handles it)
  'admin.overview.users': '{n} testers',
  'admin.overview.teams': '{n} teams',
  'admin.overview.shares': '{n} shares',
  // admin — overview page: "Connections & keys" health card (Task 11)
  'admin.overview.health.heading': 'Connections & keys',
  'admin.overview.health.secrets': 'Secrets',
  'admin.overview.health.services': 'Services',
  'admin.overview.health.present': 'present',
  'admin.overview.health.missing': 'missing',
  'admin.overview.health.group.r2': 'R2',
  'admin.overview.health.group.tg': 'Telegram',
  'admin.overview.health.group.obs': 'Sentry / PostHog',
  'admin.overview.health.group.other': 'Other',
  'admin.overview.health.d1Identity': 'D1 · identity',
  'admin.overview.health.d1Waitlist': 'D1 · waitlist',
  'admin.overview.health.r2Service': 'R2 storage',
  'admin.overview.health.serviceOk': 'ok',
  'admin.overview.health.serviceError': 'error',
  'admin.overview.health.webhookOk': 'Telegram webhook OK',
  'admin.overview.health.webhookSkip': 'not configured',
  'admin.overview.health.webhookUnexpectedUrl': 'unexpected URL',
  'admin.overview.health.releaseLabel': 'Latest release',
  'admin.overview.health.releaseNone': '—',
  'admin.overview.health.olderBuilds': '{n} testers on older builds',
  'admin.overview.health.loadError': "Couldn't load — try again.",
  // admin — observability page (Phase 6)
  'admin.obs.heading': 'Observability',
  'admin.obs.refreshBtn': 'Refresh',
  'admin.obs.freshness': 'updated {n} min ago',
  'admin.obs.freshnessNow': 'updated just now',
  'admin.obs.source.sentry': 'Sentry',
  'admin.obs.source.posthog': 'PostHog',
  'admin.obs.source.d1': 'D1',
  'admin.obs.block.health': 'Health',
  'admin.obs.block.errors': 'Errors',
  'admin.obs.block.funnel': 'Activity 24h',
  'admin.obs.block.feedback': 'Feedback',
  'admin.obs.block.versions': 'Versions',
  'admin.obs.noData': 'no data',
  'admin.obs.noEvents': 'no events in the last 24h',
  'admin.obs.noFeedback': 'no feedback in the last week',
  'admin.obs.healthOk': 'All systems operational',
  'admin.obs.healthFatal': '{n} new fatal errors in 24h',
  'admin.obs.healthIssues': '{unresolved} open issues',
  'admin.obs.loadError': "Couldn't load — try again.",
  'admin.obs.notAuthorized': 'Not authorized.',
  // login portal variant
  'loginGate.portalTitle': 'Welcome to Band Way',
  'loginGate.portalSub': "Sign in to your team's library and setlists",
  // login gate — invite-accept variant (shows the team name in invite-flavored copy)
  'loginGate.subInvite': 'Sign in to join «{teamName}»',
  // app-header (topbar)
  'topbar.report': 'Report a problem',
  'topbar.language': 'Language',
  'topbar.theme': 'Theme',
  'topbar.signIn': 'Sign in',
  'topbar.getApp': '↓ Get the app',
  'footer.shareCta': 'Want to share songs with your team?',
  'footer.getApp': 'Get the Band Way app →',
  'feedback.title': 'Report a problem',
  'feedback.placeholder': 'Describe what went wrong…',
  'feedback.submit': 'Send',
  'feedback.cancel': 'Cancel',
  'feedback.thanks': 'Thanks — your report was sent.',
  'topbar.signedIn': 'Signed in',
  'topbar.myLibrary': 'My library',
  'topbar.myLibrarySub': 'All songs shared with your team',
  'topbar.myTeam': 'My team',
  'topbar.myTeamSub': 'Members & join code',
  'topbar.admin': 'Admin',
  'topbar.signOut': 'Sign out',
  'topbar.signOutError': "Couldn't sign out. Try again.",
  'topbar.openNav': 'Open navigation',
  // error screens
  'errorScreen.revokedTitle': 'No longer available',
  'errorScreen.revokedBody': 'The owner has closed access to this recording.',
  'errorScreen.notMemberTitle': "You're not on this team",
  'errorScreen.notMemberBody': "This recording belongs to a team you're not a member of.",
  'errorScreen.notMemberBodyTeam': "This recording belongs to {team}, and you're not a member of it.",
  'errorScreen.notFoundTitle': 'No longer available',
  'errorScreen.notFoundBody': 'This share may have been deleted or its link changed.',
  'errorScreen.loadFailedTitle': "Couldn't load",
  'errorScreen.loadFailedBody': 'Something went wrong loading this recording.',
  'errorScreen.offlineTitle': 'No connection',
  'errorScreen.offlineBody': 'Check your internet connection and try again.',
  'errorScreen.retryBtn': 'Try again',
  // login gate — invitation-required / session-expired
  'loginGate.notAllowedTitle': 'Invitation required',
  'loginGate.notAllowedBody': 'Band Way is in closed alpha. Ask a team owner to invite you, or sign up at bandway.app and access opens automatically.',
  'loginGate.notAllowedCta': 'Go to bandway.app',
  'loginGate.sessionExpiredSub': 'Your session expired — sign in again.',
  // login gate — pending approval (Telegram gate, cold signup awaiting review)
  'loginGate.pendingTitle': 'Request pending',
  'loginGate.pending': "Your request is being reviewed. We'll email you when it's approved.",
  // player transport — loading progress (B-260622)
  'player.loadingTracks': '{current}/{total} tracks',
  // Song screen on the phone (part 1a of the web player redesign).
  'songScreen.close': 'Close song',
  'songScreen.more': 'More',
  'songScreen.position': '{n} of {total}',
  'songScreen.share': 'Share',
  'songScreen.cancel': 'Cancel',
  'songScreen.modeMix': 'Mix',
  'songScreen.modesLabel': 'Song view',
  'songScreen.master': 'Master',
  'songScreen.volume': '{name} volume',
  'songScreen.mute': 'Mute {name}',
  'songScreen.solo': 'Solo {name}',
  'songScreen.resetDbHint': 'Double-tap to reset to 0 dB',
  'songScreen.loading': 'Loading',
  'songScreen.muted': 'Muted',
  'songScreen.mutedBySolo': 'Muted by solo',
  'songScreen.mixMenu': 'Mix options',
  'songScreen.onlyMyPart': 'Only my part',
  'songScreen.bandWithoutMe': 'Band without me',
  'songScreen.pickPartFirst': 'Choose your part first',
  'songScreen.changeMyPart': 'Change my part…',
  'songScreen.myPartTitle': 'My part',
  'songScreen.resetMix': 'Reset mix',
  'songScreen.resetMixConfirm': 'Reset mix?',
  'songScreen.resetMixHint': 'All faders to 0 dB, mute and solo cleared',
  'songScreen.onlyThis': 'Only this track',
  'songScreen.withoutThis': 'Without this track',
  'songScreen.resetTrack': 'Reset to 0 dB',
  'songScreen.statusOnly': 'Only {name}',
  'songScreen.statusWithout': 'Without {name}',
  'songScreen.statusSolo': 'Solo: {names}',
  'songScreen.clearFocus': 'Clear',
  'songScreen.loop': 'Loop',
  'wave.loopOff': 'Turn loop off',
  'wave.zoomReset': 'Back to 1×',
  // invite accept (#5) — /invite/<token> context screen
  'invite.title': "You're invited to join {team}",
  'invite.accept': 'Accept invitation',
  'invite.expired': 'This invitation has expired. Ask the team owner to send a new one.',
  'invite.alreadyUsed': 'This invitation has already been used.',
  'invite.invalid': 'This invitation link is not valid.',
  'invite.mismatchTitle': 'Wrong account',
  'invite.mismatchBody': "This invitation was sent to {invited}. You're signed in as {current}.",
  'invite.switchAccount': 'Sign out and use the invited address',
  'invite.failed': 'Could not accept the invitation. Please try again.',
  'invite.transient': "Couldn't reach the server. Check your connection and try again.",
  'invite.deactivated': 'This account has been deactivated. Contact the team owner.',
  // chart & lyrics (wave A) — key/columns dropdowns, panel, full screen.
  // The `*Label` pair is the accessible name of a dropdown that carries no
  // visible label: the value says what it is, a screen reader gets the label.
  'chart.open': 'Chart & Lyrics',
  'chart.openLabel': 'Chart',
  'chart.openTooltip': 'Chart & lyrics',
  'chart.close': 'Close',
  'chart.fullscreen': 'Full screen',
  'chart.exitFullscreen': 'Exit full screen',
  'chart.keyOriginal': '{key} · orig.',
  'chart.keyOriginalNoKey': 'Original',
  'chart.keyLabel': 'Key',
  'chart.columns': '{n} col',
  'chart.columnsPlural': '{n} cols',
  'chart.columnsLabel': 'Columns',
  'chart.fontLabel': 'Text size',
  'chart.fontSmaller': 'Smaller text',
  'chart.fontLarger': 'Larger text',
  'chart.fontReset': 'Reset',
  'chart.settings': 'Sheet settings',
  'chart.themeLabel': 'Theme',
  'chart.themeDark': 'Dark',
  'chart.themeLight': 'Light',
  'chart.themeAuto': 'Auto',
  'chart.readingLabel': 'Reading',
  'chart.fitWholeSong': 'Fit whole song',
  'chart.followTempo': 'Follow by tempo',
  'chart.followNeedsSections': 'Needs tempo and sections in the song',
  'chart.fitDoesNotFit': "Doesn't fit on this screen",
  'chart.showLabel': 'Show',
  'chart.showChords': 'Chords',
  'chart.showLyrics': 'Lyrics',
  'chart.copyLabel': 'For slides',
  'chart.copyLyrics': 'Copy lyrics',
  'chart.copyLyricsDone': 'Copied',
  'chart.copyLyricsFailed': "Couldn't copy",
  'chart.copyLyricsNone': 'No lyrics to copy',
  'chart.copyLyricsHint': 'Lyrics only, one block per slide — paste into your presentation software.',
  'chart.followPlay': 'Start following by tempo',
  'chart.followPause': 'Pause following',
  'chart.next': 'Next song',
  'chart.prev': 'Previous song',
  'chart.empty': 'No words for this song yet',
  'chart.seek': 'Seek',
  // Текстовая ссылка на сетлист (волна B) — страница слов без аудио и без
  // логина. Строки английские во всех трёх словарях, как и chart.* выше:
  // инвариант 8 (English UI по умолчанию для новых user-facing строк).
  'textShare.untitled': 'Shared setlist',
  'textShare.songCount': '{n} songs',
  'textShare.songCountOne': '1 song',
  'textShare.wordsOnly': 'Words & chords',
  'textShare.noWords': 'No words',
  'textShare.endedTitle': 'This session has ended',
  'textShare.endedBody': 'The link was right — these words are no longer shared.',
  'textShare.notFoundTitle': 'Link not found',
  'textShare.notFoundBody': 'This link does not exist. Check it and try again.',
  'textShare.landingCta': 'About Band Way',
  // Лайв-якорь (волна C) — кнопка Live в шапке страницы слов.
  // «Нет связи» БЕЗ продолжения «считаю сам»: `stale` покрывает и период, когда
  // модель ещё выводит позицию, и период после потолка LIVE_GIVE_UP_MS, когда
  // она сдалась. Обещание, верное в половине состояния, — то же невыполненное
  // обещание, что волна B чинила отдельным коммитом (причина — live-badge.ts).
  'textShare.liveIdle': "Live hasn't started yet",
  'textShare.liveOffer': 'Live is on — tap to follow',
  'textShare.liveConnected': 'Live · connected',
  'textShare.liveStale': 'No connection',
  'textShare.nowPlaying': 'Now playing',
  // Страница слов ОДНОЙ песни (волна C, /words/<id>) — строки английские во
  // всех трёх словарях, как и textShare.* / chart.* выше (инвариант 8).
  'songWords.loading': 'Loading…',
  'songWords.forbiddenTitle': "You're not on this team",
  'songWords.forbiddenBody': "These words belong to a team you're not a member of.",
  'songWords.goneTitle': 'This publication was removed',
  'songWords.goneBody': 'The owner unpublished these words.',
  // B-260928-hardcoded-ui-strings-bypass-i18n — модалка «поделиться» и бейдж «сейчас» рейла.
  'shareModal.title': 'Share',
  'shareModal.copy': 'Copy',
  'shareModal.copied': 'Copied',
  'shareModal.copyFailed': "Couldn't copy",
  'setlist.nowBadge': 'NOW',
};

const ru: Dict = {
  'loginGate.title': 'Приватная запись',
  'loginGate.subWithTeam': '«{teamName}» поделились с тобой',
  'loginGate.subNoTeam': 'Войди, чтобы слушать',
  'loginGate.emailPlaceholder': 'your@email.com',
  'loginGate.requestBtn': 'Получить код',
  'loginGate.requestError': 'Не удалось отправить код. Попробуй ещё раз.',
  'loginGate.rateLimitRetry': 'Слишком много запросов — повторите через {wait}.',
  'common.minutesShort': '{n} мин',
  'common.secondsShort': '{n} сек',
  'loginGate.codeSent': 'Код отправлен на {email}',
  'loginGate.codePlaceholder': '6-значный код',
  'loginGate.verifyBtn': 'Войти',
  'loginGate.invalidCode': 'Неверный код',
  'loginGate.codeExpired': 'Код истёк — запроси новый',
  'loginGate.tooManyAttempts': 'Слишком много попыток — запроси новый код',
  // library
  'library.title': 'Библиотека команды',
  'library.headSummary': 'Библиотека команды · {songs} · {setlists}',
  // Обе формы совпадают — «песен: 1» верно при любом числе. Ключ-близнец
  // всё равно заведён: без него единственное число упало бы в английский.
  'library.headSongs': 'песен: {n}',
  'library.headSongsOne': 'песен: {n}',
  'library.headSetlists': 'сет-листов: {n}',
  'library.headSetlistsOne': 'сет-листов: {n}',
  'library.myTeam': 'Моя команда',
  'library.empty': 'Ты ещё не в команде',
  'library.nothingPublished': 'Пока ничего не опубликовано',
  'library.untitled': 'Без названия',
  'library.loadError': 'Не удалось загрузить библиотеку',
  'library.viewersBtn': 'Зрители',
  'library.noViewers': 'Пока никто не открывал',
  'library.viewersError': 'Не удалось загрузить зрителей',
  'library.revokeBtn': 'Отозвать',
  'library.revoked': 'Отозван',
  'library.viewerRow': '{email} · {lastSeen} · {source}',
  'library.sourceTeam': 'команда',
  'library.sourceGrant': 'напрямую',
  'library.visPublic': 'Публичная ссылка',
  'library.visTeam': 'Только команда',
  'library.visHint': 'Кто может открыть эту версию',
  'library.visOwnerNote': 'Видит только владелец версии',
  'library.lastSeenNever': 'ни разу',
  'library.visSaved': 'Сохранено',
  'library.visError': 'Не удалось сохранить',
  // W6 library keys
  'library.tabSetlists': 'Сетлисты',
  'library.tabSongs': 'Песни',
  'library.nextHeading': 'Дальше',
  'library.earlierHeading': 'Раньше',
  'library.setlistSongs': 'песен: {n}',
  'library.setlistSongsOne': 'песен: {n}',
  'library.teamSwitcherLabel': 'Команда',
  'library.setlistsEmpty': 'Пока нет сетлистов',
  'library.relToday': 'сегодня',
  'library.relTomorrow': 'завтра',
  'library.relYesterday': 'вчера',
  'library.relInDays': 'через {n} дн.',
  'library.relDaysAgo': '{n} дн. назад',
  // Волна G: офлайн-кэш — строка свежести в шапке и стирание данных
  'library.freshSyncing': 'Обновляю…',
  'library.freshJustNow': 'Обновлено только что',
  'library.freshAt': 'Обновлено {when}',
  'library.freshOfflineAt': 'Офлайн · обновлено {when}',
  'library.eraseOffline': 'Стереть офлайн-данные',
  'library.eraseOfflineConfirm': 'Стереть?',
  'library.eraseOfflineCancel': 'Оставить',
  'library.eraseOfflineDone': 'Офлайн-данные стёрты',
  // W12 visual-redesign keys
  'library.songVersions': 'Версий: {n}',
  'library.songVersionsOne': 'Версий: {n}',
  'library.songSubLatest': 'Последняя {date}',
  'library.songCount': 'Песен: {n}',
  'library.searchPlaceholder': 'Поиск песен',
  'library.songsNoMatch': 'Ничего не найдено',
  'library.nothingPublishedBody': 'Здесь появятся песни, опубликованные командой.',
  // Wave C — переключатель сортировки панели песен. Исходный язык строк —
  // английский (инвариант 8), но сами переводы обязаны быть на ru — иначе
  // одна панель показывает вперемешку "Recent | A–Z" и локализованные строки
  // ("Слушать", "нет слов", "Открывали вчера") (ревью волны C, пункт 6).
  'library.sortRecent': 'Недавние',
  'library.sortAlpha': 'А–Я',
  'library.sortCaption': 'Последний раз открывали в команде',
  // Wave C — строка песни: тело открывает слова, отдельная кнопка аудио
  'library.noWords': 'нет слов',
  'library.wordsNotPublished': 'Слова ещё не опубликованы из приложения',
  'library.wordsOpened': 'Открывали {when}',
  // Волна E — пара органов строки (см. комментарий в en-словаре).
  'library.wordsEntry': 'Слова',
  'library.audioEntry': 'Слушать',
  'library.audioVersions': 'Записи',
  'library.noWordsSlot': 'Слова этой песни ещё не опубликованы',
  'library.noAudioSlot': 'Записи этой песни ещё не опубликованы',
  // Волна F — лист управления песней (см. комментарий в en-словаре).
  'library.songSheetBtn': 'Настройки песни',
  'library.songSheetTitle': 'Настройки песни — {song}',
  'library.songSheetOwnerNote': 'Вы опубликовали эту песню',
  'library.songSheetClose': 'Закрыть',
  // ── Волна F, задача 7: раздел «Слова» в листе управления песней ─────────
  'library.wordsVisHeading': 'Кто видит слова',
  'library.wordsVisTeam': 'Только команда',
  'library.wordsVisPublic': 'Любой по ссылке',
  'library.wordsVisExplainTeam': 'Слова открываются только участникам этой команды. Остальных попросят войти.',
  'library.wordsVisExplainPublic': 'Слова откроет любой, у кого есть ссылка, в том числе вне команды. Вернёте «Только команда» — уже отправленная ссылка перестанет открываться.',
  'library.wordsLinkLabel': 'Ссылка на эти слова',
  'library.wordsLinkCopy': 'Скопировать',
  'library.wordsLinkCopied': 'Скопировано',
  'library.wordsLinkCopyFailed': 'Не удалось скопировать — выделите адрес выше и скопируйте вручную.',
  'library.wordsNeverOpened': 'Ещё никто не открывал',
  'library.wordsRemoveBtn': 'Убрать слова',
  'library.wordsRemoveWarning': 'Убрать слова из библиотеки? Уже отправленная ссылка перестанет открываться у всех.',
  'library.wordsRemoveConfirm': 'Убрать',
  'library.wordsRemoveCancel': 'Оставить',
  'library.wordsErrAuth': 'Сессия истекла — войдите заново.',
  'library.wordsErrForbidden': 'Это не ваши слова — менять их нельзя.',
  'library.wordsErrGone': 'Слова больше не опубликованы.',
  'library.wordsErrNetwork': 'Сервер не ответил — попробуйте ещё раз.',
  // ── Волна F, задача 8: раздел «Записи» в листе управления песней ────────
  'library.recordingsHeading': 'Записи',
  'library.recordingsDefaultLabel': 'Какая запись открывается первой',
  'library.recordingsDefaultBadge': 'Открывается первой',
  'library.recordingsAdded': 'добавлена {when}',
  'library.recordingsVisLabel': 'Кто откроет эту запись',
  'library.recordingsVisTeam': 'Только команда',
  'library.recordingsVisPublic': 'Любой по ссылке',
  'library.recordingsVisError': 'Не удалось сохранить — попробуйте ещё раз.',
  'library.recordingsVisReading': 'Читаем, кто может открыть…',
  'library.recordingsVisUnknown': 'Не удалось прочитать, кто может открыть.',
  'library.recordingsRemoveBtn': 'Убрать запись',
  'library.recordingsRemoveWarning': 'Убрать эту запись из библиотеки? Уже отправленная ссылка перестанет играть у всех.',
  'library.recordingsRemoveConfirm': 'Убрать',
  'library.recordingsRemoveCancel': 'Оставить',
  'library.recordingsErrAuth': 'Сессия истекла — войдите заново.',
  'library.recordingsErrForbidden': 'Это не ваша запись — менять её нельзя.',
  'library.recordingsErrGone': 'Этой записи больше нет в библиотеке.',
  'library.recordingsErrRevoked': 'Запись отозвана — первой она открываться не может.',
  'library.recordingsErrNoSong': 'Эта запись опубликована сама по себе, без песни, — первой она открываться не может.',
  'library.recordingsErrNetwork': 'Сервер не ответил — попробуйте ещё раз.',
  // ── Волна F, задача 8: раздел «Кто открывал» ────────────────────────────
  'library.sheetViewersHeading': 'Кто открывал',
  'library.sheetViewersEmpty': 'Ещё никто не открывал.',
  'library.sheetViewersError': 'Не удалось прочитать, кто открывал.',
  'library.sheetViewersRevoke': 'Убрать',
  'library.sheetViewersTeamNote': 'Команда',
  'library.sheetViewersRevokeError': 'Не удалось убрать доступ — попробуйте ещё раз.',
  'library.sheetViewersNever': 'Ещё не открывал',
  'library.sheetViewersTimes': 'раз: {n}',
  'library.sheetViewersByLink': 'по ссылке',
  'library.setlistsEmptyBody': 'Здесь появятся сет-листы команды.',
  // Wave C: скелет загрузки, состояния «нет связи»/«связь потеряна» и
  // объяснение «слов нет ни у одной песни».
  'library.loading': 'Загружаем библиотеку…',
  'library.offlineTitle': 'Нет соединения',
  'library.offlineBody': 'Проверьте интернет-соединение и попробуйте снова.',
  'library.retryBtn': 'Повторить',
  'library.connectionLostBanner': 'Связь потеряна — список может быть неактуален.',
  'library.noWordsYet': 'Слова ещё не опубликованы',
  'library.noWordsYetBody': 'Слова публикуются из приложения на компьютере, а не в вебе — опубликуйте текст там, и он появится здесь.',
  'library.importWordsBtn': 'Перенести слова из моих текстовых ссылок',
  'library.importWordsRunning': 'Переносим…',
  'library.importWordsSuccess': 'Слова добавлены для {n} песен.',
  'library.importWordsError': 'Не удалось перенести слова — попробуйте ещё раз.',
  'library.importWordsAuthError': 'Сессия истекла — войдите, чтобы перенести слова.',
  'library.importZeroNoLinks': 'У вас нет ни одной живой текстовой ссылки, откуда переносить слова.',
  'library.importZeroUpToDate': 'Слова уже актуальны — переносить нечего.',
  'library.importZeroOldLinks': 'Ваши текстовые ссылки слишком старые, чтобы прочитать из них слова автоматически — опубликуйте их заново из приложения на компьютере.',
  'library.importZeroDataGone': 'Не удалось прочитать слова из ваших ссылок — попробуйте опубликовать их заново из приложения на компьютере.',
  'library.importZeroGeneric': 'На этот раз новых слов для переноса не нашлось.',
  'common.backToLibrary': 'В библиотеку',
  'setlist.emptyBody': 'В этот сет-лист ещё не добавлены песни.',
  'setlist.notFoundBody': 'Ссылку могли отозвать или сет-лист удалили.',
  'setlist.loadErrorBody': 'Проверь соединение и попробуй ещё раз.',
  // setlist
  'setlist.empty': 'Сет-лист пуст',
  'setlist.railHeading': 'Сет-лист',
  'setlist.untitled': 'Без названия',
  'setlist.trackRow': '{pos}. {name}',
  'setlist.loadError': 'Не удалось загрузить сет-лист',
  'setlist.notFound': 'Этот сет-лист больше недоступен',
  // Единый сетлист, фаза 1 (см. комментарий в en-словаре).
  'setlistScreen.back': 'Библиотека',
  'setlistScreen.live': 'Лайв · песня {n}',
  'setlistScreen.now': 'Сейчас',
  'setlistScreen.empty': 'В этом сетлисте пока нет песен',
  'guestSetlist.captionAudioTeam': 'Слова открыты всем по ссылке. Звук — только для команды, после входа.',
  'guestSetlist.captionWordsTeam': 'Звук открыт всем по ссылке. Слова — только для команды, после входа.',
  'guestSetlist.audioLocked': 'Звук доступен команде — войдите',
  'guestSetlist.closedTitle': 'Ссылка закрыта',
  'guestSetlist.closedBodyNamed': 'Лидер закрыл гостевой доступ к «{name}». Если вы в команде — войдите, сет-лист уже в вашей библиотеке.',
  'guestSetlist.closedBodyUnnamed': 'Лидер закрыл гостевой доступ. Если вы в команде — войдите, сет-лист уже в вашей библиотеке.',
  'versionPicker.trigger': 'Версия · {label}',
  'versionPicker.ariaLabel': 'Версии',
  'versionPicker.newest': 'новейшая',
  'versionPicker.unlabeled': 'Запись',
  'versionPicker.singleMeta': '{label} · {date}',
  'versionPicker.footnote': 'Выбор сохранён на этом устройстве.',
  'versionPicker.sheetTitle': 'Версии',
  'wordsOnly.chip': 'Нет записи — только слова',
  'library.wordsTag': 'Слова',
  // dashboard
  'dashboard.back': '← Назад в библиотеку',
  'dashboard.quotaLabel': 'Использовано {used} MB из {limit} MB',
  'dashboard.joinCode': 'Код приглашения',
  'dashboard.copy': 'Скопировать',
  'dashboard.copied': 'Скопировано',
  'dashboard.copyFailed': 'Не скопировалось',
  'dashboard.members': 'Участники · {n}',
  'dashboard.roleOwner': 'Владелец',
  'dashboard.roleMember': 'Участник',
  'dashboard.loadError': 'Не удалось загрузить команду.',
  'dashboard.unknownEmail': '(неизвестно)',
  'dashboard.joinTitle': 'Вступить в команду',
  'dashboard.joinHint': 'Введи 8-значный код приглашения от владельца команды',
  'dashboard.joinPlaceholder': 'Код приглашения',
  'dashboard.joinBtn': 'Вступить',
  'dashboard.leaveBtn': 'Выйти из команды',
  'dashboard.youSuffix': '· вы',
  'dashboard.joinErrorInvalid': 'Неверный или устаревший код приглашения',
  'dashboard.joinErrorGeneric': 'Не удалось вступить — попробуй ещё раз',
  'dashboard.rotate': 'Сменить',
  'dashboard.inviteTitle': 'Пригласить по email',
  'dashboard.invitePlaceholder': 'участник@example.com',
  'dashboard.inviteBtn': 'Отправить приглашение',
  'dashboard.inviteSent': 'Приглашение отправлено',
  'dashboard.inviteError': 'Не удалось отправить — попробуй ещё раз',
  'dashboard.inviteLimit': 'Слишком много приглашений за сутки — продолжить можно завтра.',
  'dashboard.removeMember': 'Удалить участника',
  // admin
  'admin.title': 'Админ',
  'admin.nav.overview': 'Обзор',
  'admin.nav.users': 'Пользователи',
  'admin.nav.teams': 'Команды',
  'admin.usersSoon': 'Управление пользователями — скоро',
  'admin.titleTeams': 'Админ — команды',
  'admin.notAuthorized': 'Нет доступа. Войди как администратор.',
  'admin.signIn': 'Войти',
  'admin.loadError': 'Не удалось загрузить — попробуй ещё раз.',
  'admin.retry': 'Повторить',
  'admin.actionFailed': 'Действие не выполнено — попробуй ещё раз',
  'admin.storageQuota': 'Квота хранилища',
  'admin.setQuota': 'Задать квоту',
  'admin.membersHeading': 'Участники',
  'admin.noMembers': 'Нет участников',
  'admin.sharesHeading': 'Шейры',
  'admin.noShares': 'Нет активных шейров',
  'admin.back': '← Команды',
  'admin.deactivate': 'Деактивировать',
  'admin.activate': 'Активировать',
  'admin.remove': 'Удалить',
  'admin.revoke': 'Отозвать',
  'admin.membersCount': 'участников: {n}',
  'admin.teamRow': '{name} · {owner} · {used} / {limit} · {members}',
  'admin.memberRow': '{email} · {role}',
  'admin.memberRowDeactivated': '{email} · {role} · ДЕАКТИВИРОВАН',
  'admin.shareRow': '{name} · {size}',
  'common.unknown': 'неизвестно',
  'common.mb': 'МБ',
  // admin — allowlist (Users/Access section)
  'admin.allowlistHeading': 'Пользователи / Доступ',
  'admin.allowlistEmailPlaceholder': 'email@example.com',
  'admin.allowlistAdd': 'Добавить',
  'admin.allowlistRemove': 'Удалить',
  'admin.allowlistActivated': 'активирован',
  'admin.allowlistEmpty': 'Список доступа пуст',
  'admin.allowlistAddedAt': 'Добавлен {date}',
  // admin — access-requests card (Telegram gate — заявки, ждущие решения)
  'admin.pending.title': 'Запросы доступа',
  'admin.pending.approve': 'Одобрить',
  'admin.pending.reject': 'Отклонить',
  'admin.pending.empty': 'Нет запросов',
  // admin — users page (Phase 2)
  'admin.users.col.email': 'Email',
  'admin.users.col.name': 'Имя',
  'admin.users.col.status': 'Статус',
  'admin.users.col.lang': 'Язык',
  'admin.users.col.signup': 'Регистрация',
  'admin.users.col.activated': 'Активирован',
  'admin.users.col.devices': 'Устройства',
  'admin.users.col.teams': 'Команды',
  'admin.users.statusActive': 'активен',
  'admin.users.statusWaitlist': 'допущен',
  'admin.users.statusPending': 'запрос',
  'admin.users.empty': 'Пользователей пока нет',
  'admin.users.searchPlaceholder': 'Поиск по email…',
  'admin.users.loadError': 'Не удалось загрузить — попробуй ещё раз.',
  'admin.users.notAuthorized': 'Нет доступа. Войди как администратор.',
  'admin.users.statusRevoked': 'отозван',
  'admin.users.grant': 'Дать доступ',
  'admin.users.revoke': 'Забрать доступ',
  'admin.users.invite': 'Пригласить',
  'admin.users.inviteSent': 'Письмо отправлено',
  'admin.users.inviteFailed': 'Не удалось отправить — попробуйте ещё раз',
  'admin.users.actionError': 'Действие не удалось — попробуйте снова',
  // admin — delete user (Phase 5)
  'admin.users.delete': 'Удалить пользователя',
  'admin.users.confirmTitle': 'Удалить пользователя «{email}»?',
  'admin.users.confirmInstruction': 'Введи точный email для подтверждения:',
  'admin.users.confirmPlaceholder': 'Email',
  'admin.users.confirmBtn': 'Удалить навсегда',
  'admin.users.confirmCancel': 'Отмена',
  'admin.users.deleteError': 'Не удалось удалить — попробуй ещё раз',
  'admin.users.ownsTeamsError': 'Этот пользователь владеет командами. Сначала удали или переназначь их.',
  // admin — teams page (Phase 4)
  'admin.teams.col.name': 'Команда',
  'admin.teams.col.owner': 'Владелец',
  'admin.teams.col.storage': 'Хранилище',
  'admin.teams.col.members': 'Участники',
  'admin.teams.empty': 'Команд пока нет',
  'admin.teams.searchPlaceholder': 'Поиск по названию…',
  'admin.teams.loadError': 'Не удалось загрузить — попробуй ещё раз.',
  'admin.teams.notAuthorized': 'Нет доступа.',
  'admin.teams.delete': 'Удалить команду',
  'admin.teams.confirmTitle': 'Удалить команду «{name}»?',
  'admin.teams.confirmInstruction': 'Введи название команды для подтверждения:',
  'admin.teams.confirmPlaceholder': 'Название команды',
  'admin.teams.confirmBtn': 'Удалить навсегда',
  'admin.teams.confirmCancel': 'Отмена',
  'admin.teams.deleteError': 'Не удалось удалить — попробуй ещё раз',
  // admin — nav (Phase 6)
  'admin.nav.observability': 'Наблюдаемость',
  // admin — overview page (Phase 6)
  'admin.overview.heading': 'Обзор',
  'admin.overview.audience': 'Аудитория',
  'admin.overview.storage': 'Хранилище · Лимиты',
  'admin.overview.integrations': 'Интеграции',
  'admin.overview.integ.sentry': 'Sentry',
  'admin.overview.integ.posthog': 'PostHog',
  'admin.overview.integ.d1': 'D1',
  'admin.overview.integ.live': 'живой',
  'admin.overview.integ.notConfigured': 'не настроен — проверь секрет',
  // admin — plural helpers
  'admin.overview.users': '{n} тестеров',
  'admin.overview.teams': '{n} команд',
  'admin.overview.shares': '{n} шеров',
  // admin — overview page: карточка "Connections & keys" (Task 11)
  'admin.overview.health.heading': 'Подключения и ключи',
  'admin.overview.health.secrets': 'Секреты',
  'admin.overview.health.services': 'Сервисы',
  'admin.overview.health.present': 'есть',
  'admin.overview.health.missing': 'нет',
  'admin.overview.health.group.r2': 'R2',
  'admin.overview.health.group.tg': 'Telegram',
  'admin.overview.health.group.obs': 'Sentry / PostHog',
  'admin.overview.health.group.other': 'Другое',
  'admin.overview.health.d1Identity': 'D1 · identity',
  'admin.overview.health.d1Waitlist': 'D1 · waitlist',
  'admin.overview.health.r2Service': 'R2 storage',
  'admin.overview.health.serviceOk': 'ок',
  'admin.overview.health.serviceError': 'ошибка',
  'admin.overview.health.webhookOk': 'Telegram webhook в порядке',
  'admin.overview.health.webhookSkip': 'не настроен',
  'admin.overview.health.webhookUnexpectedUrl': 'неожиданный URL',
  'admin.overview.health.releaseLabel': 'Последний релиз',
  'admin.overview.health.releaseNone': '—',
  'admin.overview.health.olderBuilds': '{n} тестеров на старых версиях',
  'admin.overview.health.loadError': 'Не удалось загрузить — попробуй ещё раз.',
  // admin — observability page (Phase 6)
  'admin.obs.heading': 'Наблюдаемость',
  'admin.obs.refreshBtn': 'Обновить',
  'admin.obs.freshness': 'обновлено {n} мин назад',
  'admin.obs.freshnessNow': 'обновлено только что',
  'admin.obs.source.sentry': 'Sentry',
  'admin.obs.source.posthog': 'PostHog',
  'admin.obs.source.d1': 'D1',
  'admin.obs.block.health': 'Здоровье',
  'admin.obs.block.errors': 'Ошибки',
  'admin.obs.block.funnel': 'Активность 24ч',
  'admin.obs.block.feedback': 'Фидбэк',
  'admin.obs.block.versions': 'Версии',
  'admin.obs.noData': 'нет данных',
  'admin.obs.noEvents': 'нет событий за сутки',
  'admin.obs.noFeedback': 'за неделю нет фидбэка',
  'admin.obs.healthOk': 'Всё работает',
  'admin.obs.healthFatal': '{n} новых fatal за 24ч',
  'admin.obs.healthIssues': '{unresolved} открытых issue',
  'admin.obs.loadError': 'Не удалось загрузить — попробуй ещё раз.',
  'admin.obs.notAuthorized': 'Нет доступа.',
  // login portal variant
  'loginGate.portalTitle': 'Добро пожаловать в Band Way',
  'loginGate.portalSub': 'Войди в библиотеку и сет-листы команды',
  // login gate — invite-accept variant (shows the team name in invite-flavored copy)
  'loginGate.subInvite': 'Войди, чтобы присоединиться к «{teamName}»',
  // app-header (topbar)
  'topbar.report': 'Сообщить о проблеме',
  'topbar.language': 'Язык',
  'topbar.theme': 'Тема',
  'topbar.signIn': 'Войти',
  'topbar.getApp': '↓ Скачать приложение',
  'footer.shareCta': 'Хотите делиться песнями с командой?',
  'footer.getApp': 'Скачать приложение Band Way →',
  'feedback.title': 'Сообщить о проблеме',
  'feedback.placeholder': 'Опишите, что пошло не так…',
  'feedback.submit': 'Отправить',
  'feedback.cancel': 'Отмена',
  'feedback.thanks': 'Спасибо — отчёт отправлен.',
  'topbar.signedIn': 'Вы вошли',
  'topbar.myLibrary': 'Моя библиотека',
  'topbar.myLibrarySub': 'Все песни вашей команды',
  'topbar.myTeam': 'Моя команда',
  'topbar.myTeamSub': 'Участники и код',
  'topbar.admin': 'Админ-панель',
  'topbar.signOut': 'Выйти',
  'topbar.signOutError': 'Не удалось выйти. Попробуйте ещё раз.',
  'topbar.openNav': 'Открыть навигацию',
  // error screens
  'errorScreen.revokedTitle': 'Больше недоступно',
  'errorScreen.revokedBody': 'Владелец закрыл доступ к этой записи.',
  'errorScreen.notMemberTitle': 'Вы не в этой команде',
  'errorScreen.notMemberBody': 'Эта запись принадлежит команде, в которой вас нет.',
  'errorScreen.notMemberBodyTeam': 'Эта запись принадлежит команде «{team}», в которой вас нет.',
  'errorScreen.notFoundTitle': 'Больше недоступно',
  'errorScreen.notFoundBody': 'Возможно, эту запись удалили или ссылка изменилась.',
  'errorScreen.loadFailedTitle': 'Не удалось загрузить',
  'errorScreen.loadFailedBody': 'Что-то пошло не так при загрузке записи.',
  'errorScreen.offlineTitle': 'Нет соединения',
  'errorScreen.offlineBody': 'Проверьте интернет-соединение и попробуйте снова.',
  'errorScreen.retryBtn': 'Повторить',
  // login gate — invitation-required / session-expired
  'loginGate.notAllowedTitle': 'Доступ по приглашению',
  'loginGate.notAllowedBody': 'Band Way в закрытой альфе. Попросите владельца команды пригласить вас — или оставьте email на bandway.app, и доступ откроется автоматически.',
  'loginGate.notAllowedCta': 'Открыть bandway.app',
  'loginGate.sessionExpiredSub': 'Сессия истекла — войдите снова.',
  // login gate — pending approval (Telegram gate, cold signup awaiting review)
  'loginGate.pendingTitle': 'Заявка на рассмотрении',
  'loginGate.pending': 'Ваша заявка на рассмотрении. Мы напишем на почту, когда её одобрят.',
  // player transport — loading progress (B-260622)
  'player.loadingTracks': '{current}/{total} дорожек',
  // Экран песни на телефоне (часть 1а редизайна веб-плеера).
  'songScreen.close': 'Закрыть песню',
  'songScreen.more': 'Ещё',
  'songScreen.position': '{n} из {total}',
  'songScreen.share': 'Поделиться',
  'songScreen.cancel': 'Отмена',
  'songScreen.modeMix': 'Микс',
  'songScreen.modesLabel': 'Вид песни',
  'songScreen.master': 'Мастер',
  'songScreen.volume': 'Громкость: {name}',
  'songScreen.mute': 'Заглушить: {name}',
  'songScreen.solo': 'Соло: {name}',
  'songScreen.resetDbHint': 'Двойной тап — вернуть 0 dB',
  'songScreen.loading': 'Загрузка',
  'songScreen.muted': 'Заглушена',
  'songScreen.mutedBySolo': 'Заглушена из-за соло',
  'songScreen.mixMenu': 'Настройки микса',
  'songScreen.onlyMyPart': 'Только моя партия',
  'songScreen.bandWithoutMe': 'Группа без меня',
  'songScreen.pickPartFirst': 'Сначала выберите свою партию',
  'songScreen.changeMyPart': 'Сменить партию…',
  'songScreen.myPartTitle': 'Моя партия',
  'songScreen.resetMix': 'Сбросить микс',
  'songScreen.resetMixConfirm': 'Сбросить микс?',
  'songScreen.resetMixHint': 'Все фейдеры в 0 dB, мьюты и соло сняты',
  'songScreen.onlyThis': 'Только эта дорожка',
  'songScreen.withoutThis': 'Без этой дорожки',
  'songScreen.resetTrack': 'Вернуть 0 dB',
  'songScreen.statusOnly': 'Только {name}',
  'songScreen.statusWithout': 'Без: {name}',
  'songScreen.statusSolo': 'Соло: {names}',
  'songScreen.clearFocus': 'Снять',
  'songScreen.loop': 'Петля',
  'wave.loopOff': 'Выключить петлю',
  'wave.zoomReset': 'Вернуть 1×',
  // invite accept (#5) — /invite/<token> context screen
  'invite.title': 'Вас пригласили в команду {team}',
  'invite.accept': 'Принять приглашение',
  'invite.expired': 'Приглашение истекло. Попросите владельца команды прислать новое.',
  'invite.alreadyUsed': 'Это приглашение уже использовано.',
  'invite.invalid': 'Ссылка приглашения недействительна.',
  'invite.mismatchTitle': 'Не тот аккаунт',
  'invite.mismatchBody': 'Приглашение выписано на {invited}. Вы вошли как {current}.',
  'invite.switchAccount': 'Выйти и войти нужным адресом',
  'invite.failed': 'Не удалось принять приглашение. Попробуйте ещё раз.',
  'invite.transient': 'Не удалось связаться с сервером. Проверьте соединение и попробуйте снова.',
  'invite.deactivated': 'Этот аккаунт деактивирован. Обратитесь к владельцу команды.',
  // chart & lyrics (wave A). Translated 28.09 (B-260927-i18n-gaps-ru-uk);
  // test/i18n-translation-coverage.test.ts fails on any English copy here.
  'chart.open': 'Слова и аккорды',
  // openLabel/openTooltip are the SAME button as desktop's
  // songActions.chart.label/tooltip (app/lib/i18n/strings.ru.json) and must
  // read the same — apps/share-web/test/i18n-desktop-chart-parity.test.ts.
  'chart.openLabel': 'Аккорды',
  'chart.openTooltip': 'Слова и аккорды',
  'chart.close': 'Закрыть',
  'chart.fullscreen': 'На весь экран',
  'chart.exitFullscreen': 'Выйти из полноэкранного режима',
  'chart.keyOriginal': '{key} · ориг.',
  'chart.keyOriginalNoKey': 'Исходная',
  'chart.keyLabel': 'Тональность',
  'chart.columns': '{n} колонка',
  'chart.columnsPlural': '{n} колонки',
  'chart.columnsLabel': 'Колонки',
  'chart.fontLabel': 'Размер текста',
  'chart.fontSmaller': 'Мельче',
  'chart.fontLarger': 'Крупнее',
  'chart.fontReset': 'Сбросить',
  'chart.settings': 'Настройки листа',
  'chart.themeLabel': 'Тема',
  'chart.themeDark': 'Тёмная',
  'chart.themeLight': 'Светлая',
  'chart.themeAuto': 'Авто',
  'chart.readingLabel': 'Чтение',
  'chart.fitWholeSong': 'Вся песня на экране',
  'chart.followTempo': 'Листать по темпу',
  'chart.followNeedsSections': 'Нужны темп и секции в песне',
  'chart.fitDoesNotFit': 'Не помещается на этом экране',
  'chart.showLabel': 'Показывать',
  'chart.showChords': 'Аккорды',
  'chart.showLyrics': 'Слова',
  'chart.copyLabel': 'Для слайдов',
  'chart.copyLyrics': 'Скопировать слова',
  'chart.copyLyricsDone': 'Скопировано',
  'chart.copyLyricsFailed': 'Не удалось скопировать',
  'chart.copyLyricsNone': 'Слов для копирования нет',
  'chart.copyLyricsHint': 'Только слова, блок на слайд — вставьте в программу показа.',
  'chart.followPlay': 'Начать листать по темпу',
  'chart.followPause': 'Приостановить листание',
  'chart.next': 'Следующая песня',
  'chart.prev': 'Предыдущая песня',
  'chart.empty': 'Для этой песни пока нет слов',
  'chart.seek': 'Перемотать',
  // Текстовая ссылка на сетлист (волна B) — страница слов без аудио и без
  // логина. Строки английские во всех трёх словарях, как и chart.* выше:
  // инвариант 8 (English UI по умолчанию для новых user-facing строк).
  'textShare.untitled': 'Сетлист по ссылке',
  'textShare.songCount': 'Песен: {n}',
  'textShare.songCountOne': '1 песня',
  'textShare.wordsOnly': 'Слова и аккорды',
  'textShare.noWords': 'Нет слов',
  'textShare.endedTitle': 'Показ окончен',
  'textShare.endedBody': 'Ссылка верная — просто эти слова больше не показываются.',
  'textShare.notFoundTitle': 'Ссылка не найдена',
  'textShare.notFoundBody': 'Такой ссылки нет. Проверьте её и попробуйте ещё раз.',
  'textShare.landingCta': 'О Band Way',
  // Лайв-якорь (волна C) — кнопка Live в шапке страницы слов.
  // «Нет связи» БЕЗ продолжения «считаю сам»: `stale` покрывает и период, когда
  // модель ещё выводит позицию, и период после потолка LIVE_GIVE_UP_MS, когда
  // она сдалась. Обещание, верное в половине состояния, — то же невыполненное
  // обещание, что волна B чинила отдельным коммитом (причина — live-badge.ts).
  'textShare.liveIdle': 'Эфир ещё не начался',
  'textShare.liveOffer': 'Идёт эфир — нажмите, чтобы следить',
  'textShare.liveConnected': 'Эфир · на связи',
  'textShare.liveStale': 'Нет связи',
  'textShare.nowPlaying': 'Сейчас звучит',
  // Страница слов ОДНОЙ песни (волна C, /words/<id>) — строки английские во
  // всех трёх словарях, как и textShare.* / chart.* выше (инвариант 8).
  'songWords.loading': 'Загрузка…',
  'songWords.forbiddenTitle': 'Вы не в этой команде',
  'songWords.forbiddenBody': 'Эти слова принадлежат команде, в которой вас нет.',
  'songWords.goneTitle': 'Публикацию убрали',
  'songWords.goneBody': 'Владелец снял эти слова с публикации.',
  // B-260928-hardcoded-ui-strings-bypass-i18n — модалка «поделиться» и бейдж «сейчас» рейла.
  'shareModal.title': 'Поделиться',
  'shareModal.copy': 'Скопировать',
  'shareModal.copied': 'Скопировано',
  'shareModal.copyFailed': 'Не скопировалось',
  'setlist.nowBadge': 'СЕЙЧАС',
};

const uk: Dict = {
  'loginGate.title': 'Приватний запис',
  'loginGate.subWithTeam': '«{teamName}» поділилися з тобою',
  'loginGate.subNoTeam': 'Увійди, щоб слухати',
  'loginGate.emailPlaceholder': 'your@email.com',
  'loginGate.requestBtn': 'Отримати код',
  'loginGate.requestError': 'Не вдалося надіслати код. Спробуй ще раз.',
  'loginGate.rateLimitRetry': 'Забагато запитів — повторіть через {wait}.',
  'common.minutesShort': '{n} хв',
  'common.secondsShort': '{n} сек',
  'loginGate.codeSent': 'Код надіслано на {email}',
  'loginGate.codePlaceholder': '6-значний код',
  'loginGate.verifyBtn': 'Увійти',
  'loginGate.invalidCode': 'Невірний код',
  'loginGate.codeExpired': 'Код прострочений — запроси новий',
  'loginGate.tooManyAttempts': 'Забагато спроб — запроси новий код',
  // library
  'library.title': 'Бібліотека команди',
  'library.headSummary': 'Бібліотека команди · {songs} · {setlists}',
  'library.headSongs': 'пісень: {n}',
  'library.headSongsOne': 'пісень: {n}',
  'library.headSetlists': 'сет-листів: {n}',
  'library.headSetlistsOne': 'сет-листів: {n}',
  'library.myTeam': 'Моя команда',
  'library.empty': 'Ти ще не в команді',
  'library.nothingPublished': 'Поки нічого не опубліковано',
  'library.untitled': 'Без назви',
  'library.loadError': 'Не вдалося завантажити бібліотеку',
  'library.viewersBtn': 'Глядачі',
  'library.noViewers': 'Поки ніхто не відкривав',
  'library.viewersError': 'Не вдалося завантажити глядачів',
  'library.revokeBtn': 'Відкликати',
  'library.revoked': 'Відкликано',
  'library.viewerRow': '{email} · {lastSeen} · {source}',
  'library.sourceTeam': 'команда',
  'library.sourceGrant': 'напряму',
  'library.visPublic': 'Публічне посилання',
  'library.visTeam': 'Лише команда',
  'library.visHint': 'Хто може відкрити цю версію',
  'library.visOwnerNote': 'Бачить лише власник версії',
  'library.lastSeenNever': 'жодного разу',
  'library.visSaved': 'Збережено',
  'library.visError': 'Не вдалося зберегти',
  // W6 library keys
  'library.tabSetlists': 'Сетлисти',
  'library.tabSongs': 'Пісні',
  'library.nextHeading': 'Далі',
  'library.earlierHeading': 'Раніше',
  'library.setlistSongs': 'пісень: {n}',
  'library.setlistSongsOne': 'пісень: {n}',
  'library.teamSwitcherLabel': 'Команда',
  'library.setlistsEmpty': 'Сетлистів ще немає',
  'library.relToday': 'сьогодні',
  'library.relTomorrow': 'завтра',
  'library.relYesterday': 'вчора',
  'library.relInDays': 'через {n} дн.',
  'library.relDaysAgo': '{n} дн. тому',
  // Волна G: офлайн-кэш — строка свежести в шапке и стирание данных
  'library.freshSyncing': 'Оновлюю…',
  'library.freshJustNow': 'Оновлено щойно',
  'library.freshAt': 'Оновлено {when}',
  'library.freshOfflineAt': 'Офлайн · оновлено {when}',
  'library.eraseOffline': 'Стерти офлайн-дані',
  'library.eraseOfflineConfirm': 'Стерти?',
  'library.eraseOfflineCancel': 'Залишити',
  'library.eraseOfflineDone': 'Офлайн-дані стерто',
  // W12 visual-redesign keys
  'library.songVersions': 'Версій: {n}',
  'library.songVersionsOne': 'Версій: {n}',
  'library.songSubLatest': 'Остання {date}',
  'library.songCount': 'Пісень: {n}',
  'library.searchPlaceholder': 'Пошук пісень',
  'library.songsNoMatch': 'Нічого не знайдено',
  'library.nothingPublishedBody': 'Тут зʼявляться пісні, опубліковані командою.',
  // Wave C — перемикач сортування панелі пісень. Мова джерела рядків —
  // англійська (інваріант 8), але самі переклади мають бути на uk — інакше
  // одна панель показує впереміш "Recent | A–Z" і локалізовані рядки
  // ("Слухати", "немає слів", "Відкривали вчора") (ревʼю хвилі C, пункт 6).
  'library.sortRecent': 'Нещодавні',
  'library.sortAlpha': 'А–Я',
  'library.sortCaption': 'Востаннє відкривали в команді',
  // Wave C — рядок пісні: тіло відкриває слова, окрема кнопка аудіо
  'library.noWords': 'немає слів',
  'library.wordsNotPublished': 'Слова ще не опубліковані з застосунку',
  'library.wordsOpened': 'Відкривали {when}',
  // Волна E — пара органів рядка (див. коментар в en-словнику).
  'library.wordsEntry': 'Слова',
  'library.audioEntry': 'Слухати',
  'library.audioVersions': 'Записи',
  'library.noWordsSlot': 'Слова цієї пісні ще не опубліковані',
  'library.noAudioSlot': 'Записи цієї пісні ще не опубліковані',
  // Волна F — лист керування піснею (див. коментар в en-словнику).
  'library.songSheetBtn': 'Налаштування пісні',
  'library.songSheetTitle': 'Налаштування пісні — {song}',
  'library.songSheetOwnerNote': 'Ви опублікували цю пісню',
  'library.songSheetClose': 'Закрити',
  // ── Волна F, задача 7: раздел «Слова» в листе управления песней ─────────
  'library.wordsVisHeading': 'Хто бачить слова',
  'library.wordsVisTeam': 'Лише команда',
  'library.wordsVisPublic': 'Будь-хто за посиланням',
  'library.wordsVisExplainTeam': 'Слова відкриються лише учасникам цієї команди. Решту попросять увійти.',
  'library.wordsVisExplainPublic': 'Слова відкриє будь-хто, у кого є посилання, зокрема поза командою. Повернете «Лише команда» — уже надіслане посилання перестане відкриватися.',
  'library.wordsLinkLabel': 'Посилання на ці слова',
  'library.wordsLinkCopy': 'Скопіювати',
  'library.wordsLinkCopied': 'Скопійовано',
  'library.wordsLinkCopyFailed': 'Не вдалося скопіювати — виділіть адресу вище та скопіюйте вручну.',
  'library.wordsNeverOpened': 'Ще ніхто не відкривав',
  'library.wordsRemoveBtn': 'Прибрати слова',
  'library.wordsRemoveWarning': 'Прибрати слова з бібліотеки? Уже надіслане посилання перестане відкриватися в усіх.',
  'library.wordsRemoveConfirm': 'Прибрати',
  'library.wordsRemoveCancel': 'Залишити',
  'library.wordsErrAuth': 'Сесія завершилася — увійдіть знову.',
  'library.wordsErrForbidden': 'Це не ваші слова — змінювати їх не можна.',
  'library.wordsErrGone': 'Слова більше не опубліковані.',
  'library.wordsErrNetwork': 'Сервер не відповів — спробуйте ще раз.',
  // ── Волна F, задача 8: раздел «Записи» в листе управления песней ────────
  'library.recordingsHeading': 'Записи',
  'library.recordingsDefaultLabel': 'Який запис відкривається першим',
  'library.recordingsDefaultBadge': 'Відкривається першим',
  'library.recordingsAdded': 'додано {when}',
  'library.recordingsVisLabel': 'Хто відкриє цей запис',
  'library.recordingsVisTeam': 'Лише команда',
  'library.recordingsVisPublic': 'Будь-хто за посиланням',
  'library.recordingsVisError': 'Не вдалося зберегти — спробуйте ще раз.',
  'library.recordingsVisReading': 'Читаємо, хто може відкрити…',
  'library.recordingsVisUnknown': 'Не вдалося прочитати, хто може відкрити.',
  'library.recordingsRemoveBtn': 'Прибрати запис',
  'library.recordingsRemoveWarning': 'Прибрати цей запис із бібліотеки? Уже надіслане посилання перестане грати в усіх.',
  'library.recordingsRemoveConfirm': 'Прибрати',
  'library.recordingsRemoveCancel': 'Залишити',
  'library.recordingsErrAuth': 'Сесія завершилася — увійдіть знову.',
  'library.recordingsErrForbidden': 'Це не ваш запис — змінювати його не можна.',
  'library.recordingsErrGone': 'Цього запису більше немає в бібліотеці.',
  'library.recordingsErrRevoked': 'Запис відкликано — першим він відкриватися не може.',
  'library.recordingsErrNoSong': 'Цей запис опубліковано сам по собі, без пісні, — першим він відкриватися не може.',
  'library.recordingsErrNetwork': 'Сервер не відповів — спробуйте ще раз.',
  // ── Волна F, задача 8: раздел «Кто открывал» ────────────────────────────
  'library.sheetViewersHeading': 'Хто відкривав',
  'library.sheetViewersEmpty': 'Ще ніхто не відкривав.',
  'library.sheetViewersError': 'Не вдалося прочитати, хто відкривав.',
  'library.sheetViewersRevoke': 'Прибрати',
  'library.sheetViewersTeamNote': 'Команда',
  'library.sheetViewersRevokeError': 'Не вдалося прибрати доступ — спробуйте ще раз.',
  'library.sheetViewersNever': 'Ще не відкривав',
  'library.sheetViewersTimes': 'разів: {n}',
  'library.sheetViewersByLink': 'за посиланням',
  'library.setlistsEmptyBody': 'Тут зʼявляться сет-листи команди.',
  // Wave C: скелет завантаження, стани «немає звʼязку»/«звʼязок втрачено» та
  // пояснення «слів немає в жодної пісні».
  'library.loading': 'Завантажуємо бібліотеку…',
  'library.offlineTitle': 'Немає з’єднання',
  'library.offlineBody': 'Перевірте інтернет-з’єднання та спробуйте ще раз.',
  'library.retryBtn': 'Повторити',
  'library.connectionLostBanner': 'Зв’язок втрачено — список може бути неактуальним.',
  'library.noWordsYet': 'Слова ще не опубліковані',
  'library.noWordsYetBody': 'Слова публікуються з застосунку на комп’ютері, а не в вебі — опублікуйте текст там, і він з’явиться тут.',
  'library.importWordsBtn': 'Перенести слова з моїх текстових посилань',
  'library.importWordsRunning': 'Переносимо…',
  'library.importWordsSuccess': 'Слова додано для {n} пісень.',
  'library.importWordsError': 'Не вдалося перенести слова — спробуйте ще раз.',
  'library.importWordsAuthError': 'Сесія закінчилася — увійдіть, щоб перенести слова.',
  'library.importZeroNoLinks': 'У вас немає жодного живого текстового посилання, звідки переносити слова.',
  'library.importZeroUpToDate': 'Слова вже актуальні — переносити нема чого.',
  'library.importZeroOldLinks': 'Ваші текстові посилання застарі, щоб прочитати з них слова автоматично — опублікуйте їх знову із застосунку на комп’ютері.',
  'library.importZeroDataGone': 'Не вдалося прочитати слова з ваших посилань — спробуйте опублікувати їх знову із застосунку на комп’ютері.',
  'library.importZeroGeneric': 'Цього разу нових слів для перенесення не знайшлося.',
  'common.backToLibrary': 'До бібліотеки',
  'setlist.emptyBody': 'До цього сет-листа ще не додано пісень.',
  'setlist.notFoundBody': 'Посилання могли відкликати або сет-лист видалили.',
  'setlist.loadErrorBody': 'Перевір зʼєднання та спробуй ще раз.',
  // setlist
  'setlist.empty': 'Сет-лист порожній',
  'setlist.railHeading': 'Сет-лист',
  'setlist.untitled': 'Без назви',
  'setlist.trackRow': '{pos}. {name}',
  'setlist.loadError': 'Не вдалося завантажити сет-лист',
  'setlist.notFound': 'Цей сет-лист більше недоступний',
  // Єдиний сетлист, фаза 1 (див. коментар у en-словнику).
  'setlistScreen.back': 'Бібліотека',
  'setlistScreen.live': 'Лайв · пісня {n}',
  'setlistScreen.now': 'Зараз',
  'setlistScreen.empty': 'У цьому сет-листі поки немає пісень',
  'guestSetlist.captionAudioTeam': 'Слова відкриті всім за посиланням. Звук — лише для команди, після входу.',
  'guestSetlist.captionWordsTeam': 'Звук відкритий всім за посиланням. Слова — лише для команди, після входу.',
  'guestSetlist.audioLocked': 'Звук доступний команді — увійдіть',
  'guestSetlist.closedTitle': 'Посилання закрите',
  'guestSetlist.closedBodyNamed': 'Лідер закрив гостьовий доступ до «{name}». Якщо ви в команді — увійдіть, сет-лист уже у вашій бібліотеці.',
  'guestSetlist.closedBodyUnnamed': 'Лідер закрив гостьовий доступ. Якщо ви в команді — увійдіть, сет-лист уже у вашій бібліотеці.',
  'versionPicker.trigger': 'Версія · {label}',
  'versionPicker.ariaLabel': 'Версії',
  'versionPicker.newest': 'найновіша',
  'versionPicker.unlabeled': 'Запис',
  'versionPicker.singleMeta': '{label} · {date}',
  'versionPicker.footnote': 'Вибір збережено на цьому пристрої.',
  'versionPicker.sheetTitle': 'Версії',
  'wordsOnly.chip': 'Немає запису — лише слова',
  'library.wordsTag': 'Слова',
  // dashboard
  'dashboard.back': '← Назад до бібліотеки',
  'dashboard.quotaLabel': 'Використано {used} MB з {limit} MB',
  'dashboard.joinCode': 'Код запрошення',
  'dashboard.copy': 'Скопіювати',
  'dashboard.copied': 'Скопійовано',
  'dashboard.copyFailed': 'Не скопіювалося',
  'dashboard.members': 'Учасники · {n}',
  'dashboard.roleOwner': 'Власник',
  'dashboard.roleMember': 'Учасник',
  'dashboard.loadError': 'Не вдалося завантажити команду.',
  'dashboard.unknownEmail': '(невідомо)',
  'dashboard.joinTitle': 'Приєднатись до команди',
  'dashboard.joinHint': 'Введи 8-символьний код запрошення від власника команди',
  'dashboard.joinPlaceholder': 'Код запрошення',
  'dashboard.joinBtn': 'Приєднатись',
  'dashboard.leaveBtn': 'Вийти з команди',
  'dashboard.youSuffix': '· ти',
  'dashboard.joinErrorInvalid': 'Невірний або застарілий код запрошення',
  'dashboard.joinErrorGeneric': 'Не вдалося приєднатись — спробуй ще раз',
  'dashboard.rotate': 'Змінити',
  'dashboard.inviteTitle': 'Запросити по email',
  'dashboard.invitePlaceholder': 'учасник@example.com',
  'dashboard.inviteBtn': 'Надіслати запрошення',
  'dashboard.inviteSent': 'Запрошення надіслано',
  'dashboard.inviteError': 'Не вдалося надіслати — спробуй ще раз',
  'dashboard.inviteLimit': 'Забагато запрошень за добу — продовжити можна завтра.',
  'dashboard.removeMember': 'Видалити учасника',
  // admin
  'admin.title': 'Адмін',
  'admin.titleTeams': 'Адмін — команди',
  'admin.notAuthorized': 'Немає доступу. Увійди як адміністратор.',
  'admin.signIn': 'Увійти',
  'admin.loadError': 'Не вдалося завантажити — спробуй ще раз.',
  'admin.retry': 'Повторити',
  'admin.actionFailed': 'Дія не виконана — спробуй ще раз',
  'admin.storageQuota': 'Квота сховища',
  'admin.setQuota': 'Задати квоту',
  'admin.membersHeading': 'Учасники',
  'admin.noMembers': 'Немає учасників',
  'admin.sharesHeading': 'Посилання',
  'admin.noShares': 'Немає активних посилань',
  'admin.back': '← Команди',
  'admin.deactivate': 'Деактивувати',
  'admin.activate': 'Активувати',
  'admin.remove': 'Видалити',
  'admin.revoke': 'Відкликати',
  'admin.membersCount': 'учасників: {n}',
  'admin.teamRow': '{name} · {owner} · {used} / {limit} · {members}',
  'admin.memberRow': '{email} · {role}',
  'admin.memberRowDeactivated': '{email} · {role} · ДЕАКТИВОВАНО',
  'admin.shareRow': '{name} · {size}',
  'common.unknown': 'невідомо',
  'common.mb': 'МБ',
  // admin — allowlist (Users/Access section)
  'admin.allowlistHeading': 'Користувачі / Доступ',
  'admin.allowlistEmailPlaceholder': 'email@example.com',
  'admin.allowlistAdd': 'Додати',
  'admin.allowlistRemove': 'Видалити',
  'admin.allowlistActivated': 'активовано',
  'admin.allowlistEmpty': 'Список доступу порожній',
  'admin.allowlistAddedAt': 'Додано {date}',
  // admin — access-requests card (Telegram gate — запити, що чекають рішення)
  'admin.pending.title': 'Запити доступу',
  'admin.pending.approve': 'Схвалити',
  'admin.pending.reject': 'Відхилити',
  'admin.pending.empty': 'Немає запитів',
  // admin — users page: the four status badges of the main table. Other
  // admin.users.* keys (column headers, actions) still fall back to EN.
  'admin.users.statusActive': 'активний',
  'admin.users.statusWaitlist': 'допущений',
  'admin.users.statusPending': 'запит',
  'admin.users.statusRevoked': 'відкликаний',
  // login portal variant
  'loginGate.portalTitle': 'Ласкаво просимо до Band Way',
  'loginGate.portalSub': 'Увійди в бібліотеку та сет-листи команди',
  // login gate — invite-accept variant (shows the team name in invite-flavored copy)
  'loginGate.subInvite': 'Увійди, щоб приєднатися до «{teamName}»',
  // app-header (topbar)
  'topbar.report': 'Повідомити про проблему',
  'topbar.language': 'Мова',
  'topbar.theme': 'Тема',
  'topbar.signIn': 'Увійти',
  'topbar.getApp': '↓ Завантажити застосунок',
  'footer.shareCta': 'Хочете ділитися піснями з командою?',
  'footer.getApp': 'Завантажити застосунок Band Way →',
  'feedback.title': 'Повідомити про проблему',
  'feedback.placeholder': 'Опишіть, що пішло не так…',
  'feedback.submit': 'Надіслати',
  'feedback.cancel': 'Скасувати',
  'feedback.thanks': 'Дякуємо — звіт надіслано.',
  'topbar.signedIn': 'Ви увійшли',
  'topbar.myLibrary': 'Моя бібліотека',
  'topbar.myLibrarySub': 'Усі пісні вашої команди',
  'topbar.myTeam': 'Моя команда',
  'topbar.myTeamSub': 'Учасники та код',
  'topbar.admin': 'Адмін-панель',
  'topbar.signOut': 'Вийти',
  'topbar.signOutError': 'Не вдалося вийти. Спробуй ще раз.',
  'topbar.openNav': 'Відкрити навігацію',
  // error screens
  'errorScreen.revokedTitle': 'Більше недоступно',
  'errorScreen.revokedBody': 'Власник закрив доступ до цього запису.',
  'errorScreen.notMemberTitle': 'Ти не в цій команді',
  'errorScreen.notMemberBody': 'Цей запис належить команді, у якій тебе немає.',
  'errorScreen.notMemberBodyTeam': 'Цей запис належить команді «{team}», у якій тебе немає.',
  'errorScreen.notFoundTitle': 'Більше недоступно',
  'errorScreen.notFoundBody': 'Можливо, цей запис видалили або посилання змінилося.',
  'errorScreen.loadFailedTitle': 'Не вдалося завантажити',
  'errorScreen.loadFailedBody': 'Щось пішло не так під час завантаження запису.',
  'errorScreen.offlineTitle': 'Немає з’єднання',
  'errorScreen.offlineBody': 'Перевірте інтернет-з’єднання та спробуйте ще раз.',
  'errorScreen.retryBtn': 'Повторити',
  // login gate — invitation-required / session-expired
  'loginGate.notAllowedTitle': 'Доступ за запрошенням',
  'loginGate.notAllowedBody': 'Band Way у закритій альфі. Попросіть власника команди запросити вас — або залиште email на bandway.app, і доступ відкриється автоматично.',
  'loginGate.notAllowedCta': 'Відкрити bandway.app',
  'loginGate.sessionExpiredSub': 'Сесія завершилася — увійдіть знову.',
  // login gate — pending approval (Telegram gate, cold signup awaiting review)
  'loginGate.pendingTitle': 'Запит на розгляді',
  'loginGate.pending': 'Ваш запит на розгляді. Ми напишемо на пошту, коли його схвалять.',
  // player transport — loading progress (B-260622)
  'player.loadingTracks': '{current}/{total} доріжок',
  // Екран пісні на телефоні (частина 1а редизайну веб-плеєра).
  'songScreen.close': 'Закрити пісню',
  'songScreen.more': 'Ще',
  'songScreen.position': '{n} з {total}',
  'songScreen.share': 'Поділитися',
  'songScreen.cancel': 'Скасувати',
  'songScreen.modeMix': 'Мікс',
  'songScreen.modesLabel': 'Вигляд пісні',
  'songScreen.master': 'Майстер',
  'songScreen.volume': 'Гучність: {name}',
  'songScreen.mute': 'Заглушити: {name}',
  'songScreen.solo': 'Соло: {name}',
  'songScreen.resetDbHint': 'Подвійний тап — повернути 0 dB',
  'songScreen.loading': 'Завантаження',
  'songScreen.muted': 'Заглушена',
  'songScreen.mutedBySolo': 'Заглушена через соло',
  'songScreen.mixMenu': 'Налаштування міксу',
  'songScreen.onlyMyPart': 'Лише моя партія',
  'songScreen.bandWithoutMe': 'Гурт без мене',
  'songScreen.pickPartFirst': 'Спершу оберіть свою партію',
  'songScreen.changeMyPart': 'Змінити партію…',
  'songScreen.myPartTitle': 'Моя партія',
  'songScreen.resetMix': 'Скинути мікс',
  'songScreen.resetMixConfirm': 'Скинути мікс?',
  'songScreen.resetMixHint': 'Усі фейдери в 0 dB, мʼюти й соло зняті',
  'songScreen.onlyThis': 'Лише ця доріжка',
  'songScreen.withoutThis': 'Без цієї доріжки',
  'songScreen.resetTrack': 'Повернути 0 dB',
  'songScreen.statusOnly': 'Лише {name}',
  'songScreen.statusWithout': 'Без: {name}',
  'songScreen.statusSolo': 'Соло: {names}',
  'songScreen.clearFocus': 'Зняти',
  'songScreen.loop': 'Петля',
  'wave.loopOff': 'Вимкнути петлю',
  'wave.zoomReset': 'Повернути 1×',
  // invite accept (#5) — /invite/<token> context screen
  'invite.title': 'Вас запросили до команди {team}',
  'invite.accept': 'Прийняти запрошення',
  'invite.expired': 'Запрошення прострочене. Попросіть власника команди надіслати нове.',
  'invite.alreadyUsed': 'Це запрошення вже використано.',
  'invite.invalid': 'Посилання запрошення недійсне.',
  'invite.mismatchTitle': 'Не той обліковий запис',
  'invite.mismatchBody': 'Запрошення виписане на {invited}. Ви увійшли як {current}.',
  'invite.switchAccount': 'Вийти та увійти потрібною адресою',
  'invite.failed': 'Не вдалося прийняти запрошення. Спробуйте ще раз.',
  'invite.transient': "Не вдалося зв'язатися із сервером. Перевірте з'єднання та спробуйте ще раз.",
  'invite.deactivated': 'Цей обліковий запис деактивовано. Зверніться до власника команди.',
  // chart & lyrics (wave A). Translated 28.09 (B-260927-i18n-gaps-ru-uk);
  // test/i18n-translation-coverage.test.ts fails on any English copy here.
  'chart.open': 'Слова й акорди',
  // openLabel/openTooltip are the SAME button as desktop's
  // songActions.chart.label/tooltip (app/lib/i18n/strings.uk.json) and must
  // read the same — apps/share-web/test/i18n-desktop-chart-parity.test.ts.
  'chart.openLabel': 'Акорди',
  'chart.openTooltip': 'Слова й акорди',
  'chart.close': 'Закрити',
  'chart.fullscreen': 'На весь екран',
  'chart.exitFullscreen': 'Вийти з повноекранного режиму',
  'chart.keyOriginal': '{key} · ориг.',
  'chart.keyOriginalNoKey': 'Початкова',
  'chart.keyLabel': 'Тональність',
  'chart.columns': '{n} колонка',
  'chart.columnsPlural': '{n} колонки',
  'chart.columnsLabel': 'Колонки',
  'chart.fontLabel': 'Розмір тексту',
  'chart.fontSmaller': 'Дрібніше',
  'chart.fontLarger': 'Більше',
  'chart.fontReset': 'Скинути',
  'chart.settings': 'Налаштування аркуша',
  'chart.themeLabel': 'Тема',
  'chart.themeDark': 'Темна',
  'chart.themeLight': 'Світла',
  'chart.themeAuto': 'Авто',
  'chart.readingLabel': 'Читання',
  'chart.fitWholeSong': 'Уся пісня на екрані',
  'chart.followTempo': 'Гортати за темпом',
  'chart.followNeedsSections': 'Потрібні темп і секції в пісні',
  'chart.fitDoesNotFit': 'Не вміщується на цьому екрані',
  'chart.showLabel': 'Показувати',
  'chart.showChords': 'Акорди',
  'chart.showLyrics': 'Слова',
  'chart.copyLabel': 'Для слайдів',
  'chart.copyLyrics': 'Скопіювати слова',
  'chart.copyLyricsDone': 'Скопійовано',
  'chart.copyLyricsFailed': 'Не вдалося скопіювати',
  'chart.copyLyricsNone': 'Слів для копіювання немає',
  'chart.copyLyricsHint': 'Лише слова, блок на слайд — вставте в програму показу.',
  'chart.followPlay': 'Почати гортати за темпом',
  'chart.followPause': 'Призупинити гортання',
  'chart.next': 'Наступна пісня',
  'chart.prev': 'Попередня пісня',
  'chart.empty': 'Для цієї пісні поки немає слів',
  'chart.seek': 'Перемотати',
  // Текстовая ссылка на сетлист (волна B) — страница слов без аудио и без
  // логина. Строки английские во всех трёх словарях, как и chart.* выше:
  // инвариант 8 (English UI по умолчанию для новых user-facing строк).
  'textShare.untitled': 'Сет-лист за посиланням',
  'textShare.songCount': 'Пісень: {n}',
  'textShare.songCountOne': '1 пісня',
  'textShare.wordsOnly': 'Слова й акорди',
  'textShare.noWords': 'Немає слів',
  'textShare.endedTitle': 'Показ завершено',
  'textShare.endedBody': 'Посилання правильне — просто ці слова більше не показуються.',
  'textShare.notFoundTitle': 'Посилання не знайдено',
  'textShare.notFoundBody': 'Такого посилання немає. Перевір його й спробуй ще раз.',
  'textShare.landingCta': 'Про Band Way',
  // Лайв-якорь (волна C) — кнопка Live в шапке страницы слов.
  // «Нет связи» БЕЗ продолжения «считаю сам»: `stale` покрывает и период, когда
  // модель ещё выводит позицию, и период после потолка LIVE_GIVE_UP_MS, когда
  // она сдалась. Обещание, верное в половине состояния, — то же невыполненное
  // обещание, что волна B чинила отдельным коммитом (причина — live-badge.ts).
  'textShare.liveIdle': 'Ефір ще не почався',
  'textShare.liveOffer': 'Іде ефір — торкнись, щоб стежити',
  'textShare.liveConnected': 'Ефір · на звʼязку',
  'textShare.liveStale': 'Немає звʼязку',
  'textShare.nowPlaying': 'Зараз звучить',
  // Страница слов ОДНОЙ песни (волна C, /words/<id>) — строки английские во
  // всех трёх словарях, как и textShare.* / chart.* выше (инвариант 8).
  'songWords.loading': 'Завантаження…',
  'songWords.forbiddenTitle': 'Ти не в цій команді',
  'songWords.forbiddenBody': 'Ці слова належать команді, у якій тебе немає.',
  'songWords.goneTitle': 'Публікацію прибрали',
  'songWords.goneBody': 'Власник зняв ці слова з публікації.',
  // B-260928-hardcoded-ui-strings-bypass-i18n — модалка «поделиться» и бейдж «сейчас» рейла.
  'shareModal.title': 'Поділитися',
  'shareModal.copy': 'Скопіювати',
  'shareModal.copied': 'Скопійовано',
  'shareModal.copyFailed': 'Не скопіювалося',
  'setlist.nowBadge': 'ЗАРАЗ',
};

export const DICTS: Record<string, Dict> = { en, ru, uk };
const SUPPORTED = ['en', 'ru', 'uk'] as const;

/** Resolve a supported locale from a BCP-47 tag, defaulting to English. */
export function resolveLocale(tag: string | undefined | null): 'en' | 'ru' | 'uk' {
  const base = (tag || '').toLowerCase().split('-')[0];
  return (SUPPORTED as readonly string[]).includes(base) ? (base as 'en' | 'ru' | 'uk') : 'en';
}

const LS_KEY = 'bw-web-locale';

function readStoredLocale(): 'en' | 'ru' | 'uk' | null {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (v && (SUPPORTED as readonly string[]).includes(v)) return v as 'en' | 'ru' | 'uk';
  } catch {
    // localStorage may be unavailable (e.g. private mode quota)
  }
  return null;
}

let _locale: 'en' | 'ru' | 'uk' = 'en';
try {
  const stored = readStoredLocale();
  if (stored) {
    _locale = stored;
  } else if (typeof navigator !== 'undefined') {
    _locale = resolveLocale(navigator.language);
  }
} catch {
  _locale = 'en';
}

export function getLocale(): 'en' | 'ru' | 'uk' {
  return _locale;
}

/** Set the active locale and persist it to localStorage. Triggers a page reload
 *  in the app header so all views re-render in the chosen language. */
export function setLocale(loc: 'en' | 'ru' | 'uk'): void {
  _locale = loc;
  try {
    localStorage.setItem(LS_KEY, loc);
  } catch {
    // ignore quota / private-mode errors
  }
}

function interpolate(tpl: string, vars?: Record<string, string | number>): string {
  if (!vars) return tpl;
  return tpl.replace(/\{(\w+)\}/g, (_m, k) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : `{${k}}`,
  );
}

export function t(key: string, vars?: Record<string, string | number>): string {
  // EN is the source: fall back to it on a missing RU key, then to the key.
  const raw = DICTS[_locale][key] ?? en[key];
  if (raw === undefined) return key;
  return interpolate(raw, vars);
}

/**
 * Счётчик с формой единственного числа: при `n === 1` берётся ключ-близнец
 * `<key>One`, иначе обычный. `{n}` подставляется в обе формы.
 *
 * Множественные формы в этом i18n НЕ поддержаны как механизм — здесь закрыты
 * ровно те счётчики, у которых по-английски читалось «1 songs». Приём не
 * новый: пара `textShare.songCount` / `textShare.songCountOne` уже жила в
 * словаре, tn лишь убирает тернарник из каждого вызова.
 *
 * Близнеца нет ни в текущем словаре, ни в английском → отдаётся множественная
 * форма: иначе t() вернул бы имя ключа, и на экран уехало бы
 * «library.headSongsOne».
 *
 * Близнец ищется В ТОМ ЖЕ СЛОВАРЕ, из которого приедет базовая форма (находка
 * 13 сквозного ревью волны E). Раньше условие читало «близнец есть у локали
 * ИЛИ у английского»: если близнец существовал только по-английски, а базовый
 * ключ локаль перевела, `useOne` становился true — и t() проваливался в
 * АНГЛИЙСКИЙ текст посреди русского экрана. Обратный случай (локаль не
 * перевела счётчик вовсе, обе формы приезжают из английского) остаётся как
 * был: экран там и так английский, и «1 song» правильнее, чем «1 songs».
 */
export function tn(key: string, n: number, vars?: Record<string, string | number>): string {
  const one = key + 'One';
  const dict = DICTS[_locale][key] !== undefined ? DICTS[_locale] : en;
  const useOne = n === 1 && dict[one] !== undefined;
  return t(useOne ? one : key, { n, ...vars });
}
